"use client";

import { MouseEvent } from "react";
import { cn } from "@/lib/utils";
import { EliteLogo, GREEN_BUTTON, SHEEN_ARROW, SHEEN_LATE, SheenLabel } from "./EliteShared";
import { EliteCopy } from "./copy";

/**
 * Scrolls to the waitlist section, then focuses its email field (which opens the first-name row).
 * Focus waits for the scroll to land so the reveal plays in view; the timeout covers browsers
 * without `scrollend` and the case where the page is already there.
 */
function goToWaitlist(e: MouseEvent<HTMLAnchorElement>, targetId: string) {
  const target = document.getElementById(targetId);
  const email = target?.querySelector<HTMLInputElement>('input[name="email"]');
  if (!target || !email) return; // plain anchor jump

  e.preventDefault();
  let done = false;
  const focus = () => {
    if (done) return;
    done = true;
    window.removeEventListener("scrollend", focus);
    email.focus({ preventScroll: true });
  };
  window.addEventListener("scrollend", focus, { once: true });
  window.setTimeout(focus, 1200);
  target.scrollIntoView({ behavior: "smooth", block: "start" });
}

/** Floating black bar. The CTA jumps to the final waitlist section. */
export default function EliteNav({
  ctaText,
  targetId,
  copy,
}: {
  ctaText?: string;
  targetId: string;
  copy: EliteCopy;
}) {
  return (
    <nav
      aria-label="IQ Elite"
      className="fixed top-[2rem] right-[2rem] left-[2rem] z-50 flex h-[7.25rem] items-center justify-between rounded-[1.375rem] bg-black py-[0.75rem] pr-[0.75rem] pl-[2.375rem] drop-shadow-[0_0.5rem_2.1188rem_rgba(0,0,0,0.11)] lg:top-[1.625rem] lg:right-auto lg:left-1/2 lg:w-[129.25rem] lg:-translate-x-1/2 lg:pl-[2.75rem] lg:shadow-[0_0.5rem_4.2375rem_0_rgba(0,0,0,0.11)] lg:drop-shadow-none">
      <a href="#top" aria-label={copy.backToTop}>
        <EliteLogo tone="light" />
      </a>
      <a
        href={`#${targetId}`}
        onClick={(e) => goToWaitlist(e, targetId)}
        className={cn(
          GREEN_BUTTON,
          SHEEN_LATE,
          "font-geist flex h-[5.75rem] items-center gap-[2rem] rounded-[0.875rem] pr-[0.5rem] pl-[3rem] text-[1.75rem] font-semibold whitespace-nowrap text-black lg:gap-[1.25rem]",
        )}>
        <SheenLabel>{ctaText ?? copy.joinWaitlist}</SheenLabel>
        <span className="flex h-[4.75rem] w-[4.75rem] items-center justify-center rounded-[0.5rem]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/elite/arrow-btn.svg" alt="" aria-hidden="true" className={cn("h-[1.5rem] w-[1.75rem]", SHEEN_ARROW)} />
        </span>
      </a>
    </nav>
  );
}
